/* prettier-ignore-file */
import React, { useState, useEffect } from "react";
import { 
  View, 
  Text, 
  StyleSheet, 
  ScrollView, 
  TouchableOpacity,
  ActivityIndicator,
} from "react-native";
import EditableSection from "./EditableSection";
import EmailPreview from "./EmailPreview";

const EmailTemplateEditor = ({ 
  templateType = "delivery", // "delivery", "homePickup", "inStore", "bookingEdit", "cancellationRequest", "penalty"
  initialData = {},
  onSave,
  saving = false,
}) => {
  const [mainTitle, setMainTitle] = useState("");
  const [headerSubtitle, setHeaderSubtitle] = useState("");
  const [greetingText, setGreetingText] = useState("");
  const [customInstructions, setCustomInstructions] = useState("");
  const [warningText, setWarningText] = useState("");
  const [buttonText, setButtonText] = useState("");
  const [footerMessage, setFooterMessage] = useState("");
  // Refund-specific fields
  const [stripeRefundReceiptLink, setStripeRefundReceiptLink] = useState("");
  const [refundAmount, setRefundAmount] = useState("");
  const [hasChanges, setHasChanges] = useState(false);

  // Load initial data
  useEffect(() => {
    if (initialData) {
      setMainTitle(initialData.mainTitle || getDefaultMainTitle());
      setHeaderSubtitle(initialData.headerSubtitle || getDefaultHeaderSubtitle());
      setGreetingText(initialData.greetingText || getDefaultGreeting());
      // refundOnly and stripeReceipt use customMessage, others use customInstructions
      setCustomInstructions(
        templateType === "refundOnly" || templateType === "stripeReceipt"
          ? (initialData.customMessage || "")
          : (initialData.customInstructions || "")
      );
      setWarningText(initialData.warningText || getDefaultWarning());
      // Replace "Manage Your Booking" with "View Your Booking" if present
      let buttonTextValue = initialData.buttonText || getDefaultButtonText();
      if (buttonTextValue && buttonTextValue.includes("Manage Your Booking")) {
        buttonTextValue = buttonTextValue.replace(/Manage Your Booking/g, "View Your Booking");
      }
      setButtonText(buttonTextValue);
      setFooterMessage(initialData.footerMessage || "");
      // Refund-specific fields
      setStripeRefundReceiptLink(initialData.stripeRefundReceiptLink || "");
      setRefundAmount(initialData.refundAmount || "");
      // Stripe receipt-specific fields
      if (templateType === "stripeReceipt") {
        setStripeRefundReceiptLink(initialData.stripeReceiptLink || "");
        setRefundAmount(initialData.paymentAmount || "");
      }
    }
  }, [initialData, templateType]);

  // Track changes
  useEffect(() => {
    const isCancellationRequest = templateType === "cancellationRequest";
    const isRefundTemplate = templateType === "refundOnly" || templateType === "bookingEdit";
    const isStripeReceiptTemplate = templateType === "stripeReceipt";
    const hasAnyChanges = 
      mainTitle !== (initialData.mainTitle || getDefaultMainTitle()) ||
      headerSubtitle !== (initialData.headerSubtitle || getDefaultHeaderSubtitle()) ||
      greetingText !== (initialData.greetingText || getDefaultGreeting()) ||
      (isCancellationRequest ? warningText !== (initialData.warningText || getDefaultWarning()) : 
       templateType === "refundOnly" || templateType === "stripeReceipt" ? customInstructions !== (initialData.customMessage || "") :
       customInstructions !== (initialData.customInstructions || "")) ||
      buttonText !== (initialData.buttonText || getDefaultButtonText()) ||
      footerMessage !== (initialData.footerMessage || "") ||
      (isRefundTemplate && (
        stripeRefundReceiptLink !== (initialData.stripeRefundReceiptLink || "") ||
        refundAmount !== (initialData.refundAmount || "")
      )) ||
      (isStripeReceiptTemplate && (
        stripeRefundReceiptLink !== (initialData.stripeReceiptLink || "") ||
        refundAmount !== (initialData.paymentAmount || "")
      ));
    
    setHasChanges(hasAnyChanges);
  }, [mainTitle, headerSubtitle, greetingText, customInstructions, warningText, buttonText, footerMessage, stripeRefundReceiptLink, refundAmount, initialData, templateType]);

  const getDefaultMainTitle = () => {
    switch (templateType) {
      case "homePickup":
        return "🚴 Booking Confirmed!";
      case "inStore":
        return "🚴 Booking Confirmed!";
      case "bookingEdit":
        return "✏️ Booking Updated";
      case "cancellationRequest":
        return "🚫 Cancellation Request";
      case "cancellationConfirmation":
        return "🚫 Booking Cancelled";
      case "refundOnly":
        return "💰 Refund Processed";
      case "thankYou":
        return "🙏 Thank You!";
      case "penalty":
        return "⚖️ Equipment Charges";
      case "stripeReceipt":
        return "Payment Receipt";
      case "universal":
        return "🚴 Booking Confirmed!";
      default:
        return "🚴 Booking Confirmed!";
    }
  };

  const getDefaultHeaderSubtitle = () => {
    switch (templateType) {
      case "homePickup":
        return "We'll handle pickup and delivery for you!";
      case "inStore":
        return "Your bikes are ready for pickup at our store";
      case "bookingEdit":
        return "Changes have been made to your reservation";
      case "cancellationRequest":
        return "We received your cancellation request";
      case "cancellationConfirmation":
        return "We're sorry to see you go";
      case "refundOnly":
        return "Your refund has been processed";
      case "thankYou":
        return "Thank you for choosing {storeName}!";
      case "penalty":
        return "We hope you had a great rental!";
      case "stripeReceipt":
        return "Your payment receipt is ready";
      case "universal":
        return "Get ready for your adventure with {storeName}";
      default:
        return "Get ready for your adventure with {storeName}";
    }
  };

  const getDefaultGreeting = () => {
    switch (templateType) {
      case "homePickup":
        return "Great news! Your bike rental has been confirmed and payment received. We'll deliver your bikes to you and pick them up when you're done - no hassle! 🎉";
      case "inStore":
        return "Great news! Your bike rental has been confirmed and payment received. Your bikes are ready for pickup at our store during your scheduled time. 🎉";
      case "bookingEdit":
        return "Your booking has been successfully updated. Review the changes below and contact us if you have any questions.";
      case "cancellationRequest":
        return "We received a request to cancel your booking. To complete the cancellation, please click the button below and confirm.";
      case "cancellationConfirmation":
        return "Your booking has been successfully cancelled and your refund has been processed.";
      case "refundOnly":
        return "We've processed a refund for your booking.";
      case "thankYou":
        return "Thank you for your rental! We hope you had a wonderful experience. We'd love to hear your feedback!";
      case "penalty":
        return "Thank you for choosing {storeName}! We've completed your check-in and found it necessary to charge for some equipment-related items.";
      case "stripeReceipt":
        return "Thank you for your payment. Your receipt is available below.";
      case "universal":
        return "Great news! Your bike rental has been confirmed and payment received.";
      default:
        return "Great news! Your bike rental has been confirmed and payment received. We can't wait to get you out on the trails! 🎉";
    }
  };

  const getDefaultWarning = () => {
    if (templateType === "cancellationRequest") {
      return "Cancelling your booking will remove it from the system and issue a refund according to our cancellation policy. This action cannot be undone.";
    }
    return "";
  };

  const getDefaultButtonText = () => {
    if (templateType === "cancellationRequest") {
      return "🚫 Confirm Cancellation";
    }
    if (templateType === "cancellationConfirmation") {
      return "📞 Contact Us";
    }
    if (templateType === "refundOnly") {
      return "✏️ View Your Booking";
    }
    if (templateType === "bookingEdit") {
      return "✏️ View Your Booking";
    }
    if (templateType === "thankYou") {
      return "📋 View Your Booking";
    }
    if (templateType === "penalty") {
      return "📋 View Check-In Details";
    }
    if (templateType === "stripeReceipt") {
      return "View Booking";
    }
    return "✏️ View Your Booking";
  };

  const handleSave = () => {
    if (onSave) {
      const dataToSave = {
        mainTitle,
        headerSubtitle,
        greetingText,
        buttonText,
        footerMessage,
      };
      
      // Add customInstructions OR warningText OR customMessage depending on template type
      if (templateType === "cancellationRequest") {
        dataToSave.warningText = warningText;
      } else if (templateType === "refundOnly" || templateType === "stripeReceipt") {
        dataToSave.customMessage = customInstructions; // refundOnly and stripeReceipt use customMessage
      } else {
        dataToSave.customInstructions = customInstructions;
      }
      
      // Add refund-specific fields for refund templates
      if (templateType === "refundOnly" || templateType === "bookingEdit") {
        dataToSave.stripeRefundReceiptLink = stripeRefundReceiptLink;
        dataToSave.refundAmount = refundAmount;
      }
      
      // Add Stripe receipt-specific fields
      if (templateType === "stripeReceipt") {
        dataToSave.stripeReceiptLink = stripeRefundReceiptLink;
        dataToSave.paymentAmount = refundAmount;
      }
      
      onSave(dataToSave);
    }
  };

  const getTemplateName = () => {
    switch (templateType) {
      case "homePickup":
        return "Home Pickup Confirmation";
      case "inStore":
        return "In-Store Pickup Confirmation";
      case "bookingEdit":
        return "Booking Edit Confirmation";
      case "cancellationRequest":
        return "Cancellation Request";
      case "thankYou":
        return "Thank You Message";
      case "universal":
        return "Booking Confirmation";
      case "refundOnly":
        return "Refund Confirmation";
      case "cancellationConfirmation":
        return "Cancellation Confirmation";
      case "penalty":
        return "Equipment Penalty Charge";
      case "stripeReceipt":
        return "Stripe Receipt";
      default:
        return "Delivery Confirmation";
    }
  };

  return (
    <View style={styles.container}>
      {/* Editor Panel */}
      <ScrollView style={styles.editorPanel}>
        <View style={styles.editorHeader}>
          <Text style={styles.editorTitle}>✏️ Edit {getTemplateName()}</Text>
          <Text style={styles.editorSubtitle}>
            Customize the text below. Layout and variables are automatically managed.
          </Text>
        </View>

        <EditableSection
          label="Main Title"
          value={mainTitle}
          onChange={setMainTitle}
          hint="Main title in the email header (e.g., '🚴 Booking Confirmed!')"
          variables={[]}
          placeholder={getDefaultMainTitle()}
        />

        {templateType !== "universal" && (
          <EditableSection
            label="Header Subtitle"
            value={headerSubtitle}
            onChange={setHeaderSubtitle}
            hint="Appears in the purple header below the main title"
            variables={["{storeName}"]}
            placeholder={getDefaultHeaderSubtitle()}
          />
        )}

        <EditableSection
          label={templateType === "universal" ? "Intro Message" : "Greeting Message"}
          value={greetingText}
          onChange={setGreetingText}
          hint="Opening paragraph after 'Hi {customerName}'"
          variables={["{storeName}", "{customerName}"]}
          multiline
          placeholder={getDefaultGreeting()}
        />

        {templateType !== "universal" && (
          <>
            {templateType === "cancellationRequest" ? (
              <EditableSection
                label="Warning Text"
                value={warningText}
                onChange={setWarningText}
                hint="Important warning displayed in yellow box before the button"
                variables={["{storeName}"]}
                multiline
                placeholder="Cancelling your booking will remove it from the system..."
              />
            ) : (
              <EditableSection
                label="Custom Instructions (Optional)"
                value={customInstructions}
                onChange={setCustomInstructions}
                hint="Additional information you want to communicate (appears below cost summary)"
                variables={["{storeName}", "{storePhone}", "{storeEmail}", "{startDate}", "{endDate}"]}
                multiline
                placeholder="e.g., Please arrive 10 minutes before your scheduled time..."
              />
            )}

            <EditableSection
              label="Button Text"
              value={buttonText}
              onChange={setButtonText}
              hint="Text on the main action button"
              placeholder={getDefaultButtonText()}
            />

            {/* Refund-specific fields */}
            {(templateType === "refundOnly" || templateType === "bookingEdit") && (
              <>
                <EditableSection
                  label="Stripe Refund Receipt Link (Optional)"
                  value={stripeRefundReceiptLink}
                  onChange={setStripeRefundReceiptLink}
                  hint="Link to Stripe refund receipt (will be displayed as a button)"
                  variables={[]}
                  placeholder="https://pay.stripe.com/receipts/..."
                />
                <EditableSection
                  label="Refund Amount (Optional)"
                  value={refundAmount}
                  onChange={setRefundAmount}
                  hint="Refund amount to display (e.g., '$50.00')"
                  variables={[]}
                  placeholder="$0.00"
                />
              </>
            )}

            {/* Stripe Receipt-specific fields */}
            {templateType === "stripeReceipt" && (
              <>
                <EditableSection
                  label="Stripe Receipt Link (Optional)"
                  value={stripeRefundReceiptLink}
                  onChange={setStripeRefundReceiptLink}
                  hint="Link to Stripe payment receipt (will be displayed as a button)"
                  variables={[]}
                  placeholder="https://pay.stripe.com/receipts/..."
                />
                <EditableSection
                  label="Payment Amount (Optional)"
                  value={refundAmount}
                  onChange={setRefundAmount}
                  hint="Payment amount processed by Stripe"
                  variables={[]}
                  placeholder="$50.00"
                />
              </>
            )}

            <EditableSection
              label="Footer Message (Optional)"
              value={footerMessage}
              onChange={setFooterMessage}
              hint="Final message before the copyright footer"
              variables={["{storeName}"]}
              multiline
              placeholder="e.g., Thank you for choosing {storeName}! We look forward to your adventure."
            />
          </>
        )}

        {hasChanges && (
          <TouchableOpacity dataSet={{ hover: "lift" }}
            style={[styles.saveButton, saving && styles.saveButtonDisabled]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color="#FFFFFF" />
            ) : (
              <Text style={styles.saveButtonText}>💾 Save Template</Text>
            )}
          </TouchableOpacity>
        )}

        <View style={styles.infoBox}>
          <Text style={styles.infoTitle}>ℹ️ How This Works</Text>
          <Text style={styles.infoText}>
            • Edit the text in any section{"\n"}
            • Use variables like {"{customerName}"} for dynamic content{"\n"}
            • The preview shows how it will look to customers{"\n"}
            • Layout, colors, and structure are fixed for consistency{"\n"}
            • Changes are saved separately from the HTML
          </Text>
        </View>
      </ScrollView>

      {/* Preview Panel */}
      <View style={styles.previewPanel}>
        <View style={styles.previewHeader}>
          <Text style={styles.previewTitle}>📧 Live Preview</Text>
          <Text style={styles.previewSubtitle}>This is what customers will see</Text>
        </View>
        <EmailPreview
          templateType={templateType}
          mainTitle={mainTitle}
          headerSubtitle={headerSubtitle}
          greetingText={greetingText}
          customInstructions={customInstructions}
          buttonText={buttonText}
          footerMessage={footerMessage}
          stripeRefundReceiptLink={stripeRefundReceiptLink}
          refundAmount={refundAmount}
          stripeReceiptLink={templateType === "stripeReceipt" ? stripeRefundReceiptLink : ""}
          paymentAmount={templateType === "stripeReceipt" ? refundAmount : ""}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    flex: 1,
    backgroundColor: "#F6F8F7",
  },
  editorPanel: {
    flex: 1,
    padding: 20,
    backgroundColor: "#F4F6F6",
  },
  editorHeader: {
    marginBottom: 25,
    paddingBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#0E7C6B",
  },
  editorTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 8,
  },
  editorSubtitle: {
    fontSize: 14,
    color: "#5C6B68",
    lineHeight: 20,
  },
  saveButton: {
    backgroundColor: "#1F8A5B",
    padding: 16,
    borderRadius: 6,
    alignItems: "center",
    marginVertical: 20,
    boxShadow: "0 2px 6px rgba(15, 26, 25, 0.08), 0 16px 40px rgba(15, 26, 25, 0.12)",
    elevation: 4,
  },
  saveButtonDisabled: {
    backgroundColor: "#5C6B68",
    opacity: 0.6,
  },
  saveButtonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "bold",
  },
  infoBox: {
    backgroundColor: "#E3F2EF",
    borderLeftWidth: 4,
    borderLeftColor: "#0E7C6B",
    padding: 15,
    borderRadius: 6,
    marginTop: 20,
    marginBottom: 40,
  },
  infoTitle: {
    fontSize: 15,
    fontWeight: "600",
    color: "#0E7C6B",
    marginBottom: 10,
  },
  infoText: {
    fontSize: 13,
    color: "#2E3A38",
    lineHeight: 20,
  },
  previewPanel: {
    flex: 1,
    backgroundColor: "#DDE3E2",
    borderLeftWidth: 2,
    borderLeftColor: "#DDE3E2",
  },
  previewHeader: {
    backgroundColor: "#2E3A38",
    padding: 20,
    borderBottomWidth: 2,
    borderBottomColor: "#5C6B68",
  },
  previewTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#FFFFFF",
    marginBottom: 4,
  },
  previewSubtitle: {
    fontSize: 13,
    color: "#DDE3E2",
  },
});

export default EmailTemplateEditor;

